import { supabase } from '@/lib/supabase';
import { addImageResizeParams, ImageSizePresets } from '@/utils/imageResizer';
import { trackAddToFavorites, trackRemoveFromFavorites } from '@/src/analytics/userInteraction';
import { favoritesCacheService } from '@/services/favoritesCacheService';
import { episodeDetailsCacheService } from '@/services/episodeDetailsCacheService';
import { isUnifiedContentEnabled } from '@/config/featureFlags';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const favLog = createScopedLogger('FAVORITES-SVC');

export interface UserFavorite {
  id: string;
  smartuser_id: string;
  content_id: number;
  content_type: 'serie' | 'episode';
  created_at: string;
  updated_at: string;
}

export interface FavoriteContent {
  id: string;
  contentDbId: number;
  title: string;
  description: string;
  thumbnail: string;
  type: 'serie' | 'episode';
  created_at: string;
  // Additional fields for episodes
  episodeNumber?: number;
  duration?: number;
  seasonNumber?: number;
  serieTitle?: string;
  serieId?: string;
}

export class FavoritesService {
  private static inFlightRequests: Map<string, Promise<FavoriteContent[]>> = new Map();

  // Add content to favorites
  static async addToFavorites(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<boolean> {
    const result = await favoritesCacheService.addFavorite(smartuserId, contentId, contentType);

    if (result.success) {
      console.log('✅ Successfully added to favorites:', { contentId, contentType });
    } else {
      console.error('❌ Error adding to favorites:', result.error);
    }

    // Track analytics event
    if (result.success) {
      try {
        await trackAddToFavorites(contentId, contentType);
      } catch (analyticsError) {
        console.error('Failed to track add_favorites event:', analyticsError);
      }
    }

    return result.success;
  }

  // Remove content from favorites
  static async removeFromFavorites(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<boolean> {
    const result = await favoritesCacheService.removeFavorite(smartuserId, contentId, contentType);

    if (result.success) {
      console.log('✅ Successfully removed from favorites:', { contentId, contentType });
    } else {
      console.error('❌ Error removing from favorites:', result.error);
    }

    // Track analytics event
    if (result.success) {
      try {
        await trackRemoveFromFavorites(contentId, contentType);
      } catch (analyticsError) {
        console.error('Failed to track remove_favorites event:', analyticsError);
      }
    }

    return result.success;
  }

  // Check if content is in favorites
  static async isFavorite(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<boolean> {
    const isFav = await favoritesCacheService.isFavorite(contentId, contentType);
    return isFav;
  }

  // Get user's favorite content with details
  static async getUserFavorites(
    campaignCountriesLanguagesId: string,
    smartuserId: string
  ): Promise<FavoriteContent[]> {
    const requestKey = `${smartuserId}_${campaignCountriesLanguagesId}`;

    const existingRequest = this.inFlightRequests.get(requestKey);
    if (existingRequest) {
      console.log(`⭐ [FAVORITES-DEDUP] Reusing in-flight request for user ${smartuserId}`);
      return existingRequest;
    }

    const requestPromise = this.executeGetUserFavorites(campaignCountriesLanguagesId, smartuserId);
    this.inFlightRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.inFlightRequests.delete(requestKey);
    }
  }

  private static async executeGetUserFavorites(
    campaignCountriesLanguagesId: string,
    smartuserId: string
  ): Promise<FavoriteContent[]> {
    const startTime = Date.now();
    console.log(`🔍 [${new Date().toISOString()}] FavoritesService.getUserFavorites called with:`, {
      campaignCountriesLanguagesId,
      smartuserId
    });

    if (!supabase) {
      console.warn('Supabase not configured');
      return [];
    }

    try {
      await favoritesCacheService.initialize(parseInt(smartuserId));

      const cachedFavorites = await favoritesCacheService.getAllFavorites();

      if (cachedFavorites && cachedFavorites.length > 0) {
        console.log(`⭐ [FAVORITES-CACHE] ✅ Found ${cachedFavorites.length} favorites in cache, using cache-first approach`);

        // Separate series and episodes from cache
        const cachedSeries = cachedFavorites.filter(f => f.content_type === 'serie');
        const cachedEpisodes = cachedFavorites.filter(f => f.content_type === 'episode');

        const favorites: FavoriteContent[] = [];

        // Process series favorites from cache
        if (cachedSeries.length > 0) {
          const seriesIds = cachedSeries.map(f => f.content_id);
          console.log(`⭐ [FAVORITES-CACHE] Processing ${seriesIds.length} series from cache`);

          const { data: seriesDetails, error: seriesDetailsError } = await withSupabaseResult<any[]>(
            supabase
              .from('contents_series')
              .select('serie_id, title, description, url_covers')
              .in('serie_id', seriesIds)
              .eq('campaign_countries_languages_id', campaignCountriesLanguagesId),
            { operation: 'favorites.cache.seriesDetails', logger: favLog, fallback: [] }
          );

          if (seriesDetailsError) {
            console.error('⭐ [FAVORITES-CACHE] Error fetching series details:', seriesDetailsError);
          } else if (seriesDetails) {
            cachedSeries.forEach((favorite) => {
              const serie = seriesDetails.find(s => s.serie_id === favorite.content_id);
              if (serie) {
                favorites.push({
                  id: favorite.id,
                  contentDbId: serie.serie_id,
                  title: serie.title,
                  description: serie.description || 'No description available',
                  thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.serieCardVertical, `Favorite Serie: ${serie.title}`),
                  type: 'serie',
                  created_at: favorite.created_at,
                });
              }
            });
          }
        }

        // Process episodes favorites from cache (already optimized with episode cache)
        if (cachedEpisodes.length > 0) {
          const episodeIds = cachedEpisodes.map(f => f.content_id);
          console.log(`⭐ [FAVORITES-CACHE] Processing ${episodeIds.length} episodes from cache`);

          const cachedEpisodeDetails = await episodeDetailsCacheService.getBulkEpisodeDetails(
            campaignCountriesLanguagesId,
            episodeIds
          );

          const missingEpisodeIds = episodeIds.filter(id => !cachedEpisodeDetails.has(id));

          let episodeDetails: any[] = [];

          if (missingEpisodeIds.length > 0) {
            console.log(`⭐ [FAVORITES-CACHE] Fetching ${missingEpisodeIds.length} missing episode details from database`);

            const useUnified = isUnifiedContentEnabled();
            console.log(`⭐ [FAVORITES-CACHE] Using ${useUnified ? 'unified' : 'legacy'} content system`);

            if (useUnified) {
              console.log('⭐ [FAVORITES-CACHE] Unified content system not yet fully implemented for favorites');
            }

            const { data: fetchedDetails, error: episodeDetailsError } = await withSupabaseResult<any[]>(
              supabase
                .from('contents_series_episodes')
                .select(`
                  episode_id,
                  title,
                  description,
                  episode_position,
                  season_position,
                  duration,
                  series_id,
                  contents_series!inner(
                    title,
                    url_covers
                  )
                `)
                .in('episode_id', missingEpisodeIds)
                .eq('campaign_countries_languages_id', campaignCountriesLanguagesId),
              { operation: 'favorites.cache.episodeDetails', logger: favLog, fallback: [] }
            );

            if (episodeDetailsError) {
              console.error('⭐ [FAVORITES-CACHE] Error fetching episode details:', episodeDetailsError);
            } else if (fetchedDetails) {
              await episodeDetailsCacheService.setBulkEpisodeDetails(
                campaignCountriesLanguagesId,
                fetchedDetails
              );
              episodeDetails = fetchedDetails;
            }
          }

          const allEpisodeDetails = new Map<number, any>();
          cachedEpisodeDetails.forEach((details, id) => allEpisodeDetails.set(id, details));
          episodeDetails.forEach((details: any) => allEpisodeDetails.set(details.episode_id, details));

          cachedEpisodes.forEach((favorite) => {
            const episode = allEpisodeDetails.get(favorite.content_id);
            if (episode) {
              const serie = episode.contents_series;
              favorites.push({
                id: favorite.id,
                contentDbId: episode.episode_id,
                title: episode.title || `Episode ${episode.episode_position}`,
                description: episode.description || `Episode ${episode.episode_position} description`,
                thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.serieCardVertical, `Favorite Episode: ${serie.title} S${episode.season_position}E${episode.episode_position}`),
                type: 'episode',
                created_at: favorite.created_at,
                episodeNumber: episode.episode_position,
                duration: Math.floor((episode.duration || 900) / 60),
                seasonNumber: episode.season_position,
                serieTitle: serie.title,
                serieId: episode.series_id.toString(),
              });
            }
          });
        }

        favorites.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        const duration = Date.now() - startTime;
        console.log(`⭐ [FAVORITES-CACHE] ✅ Loaded ${favorites.length} favorites from cache in ${duration}ms (avoided 2 DB queries)`);

        return favorites;
      }

      // CACHE MISS: Fall back to database queries
      console.log(`⭐ [FAVORITES-CACHE] ❌ No favorites in cache, fetching from database`);

      const favorites: FavoriteContent[] = [];

      console.log(`🔍 [${new Date().toISOString()}] Fetching favorite series...`);
      // Get favorite series
      const { data: favoriteSeries, error: seriesError } = await withSupabaseResult<any[]>(
        supabase
          .from('user_favorites')
          .select('*')
          .eq('smartuser_id', smartuserId)
          .eq('content_type', 'serie')
          .order('created_at', { ascending: false }),
        { operation: 'favorites.db.favoriteSeries', logger: favLog, fallback: [] }
      );

      console.log(`🔍 [${new Date().toISOString()}] Favorite series query result:`, {
        data: favoriteSeries,
        error: seriesError,
        count: favoriteSeries?.length || 0
      });

      if (seriesError) {
        console.error('Error fetching favorite series:', seriesError);
      } else if (favoriteSeries) {
        // Get series details for favorite series
        const seriesIds = favoriteSeries.map(fav => fav.content_id);
        console.log(`🔍 [${new Date().toISOString()}] Series IDs to fetch details for:`, seriesIds);
        
        if (seriesIds.length > 0) {
          const { data: seriesDetails, error: seriesDetailsError } = await withSupabaseResult<any[]>(
            supabase
              .from('contents_series')
              .select('serie_id, title, description, url_covers')
              .in('serie_id', seriesIds)
              .eq('campaign_countries_languages_id', campaignCountriesLanguagesId),
            { operation: 'favorites.db.seriesDetails', logger: favLog, fallback: [] }
          );

          console.log(`🔍 [${new Date().toISOString()}] Series details query result:`, {
            data: seriesDetails,
            error: seriesDetailsError,
            count: seriesDetails?.length || 0
          });

          if (seriesDetailsError) {
            console.error('Error fetching series details:', seriesDetailsError);
          } else if (seriesDetails) {
            favoriteSeries.forEach((favorite: any) => {
              const serie = seriesDetails.find(s => s.serie_id === favorite.content_id);
              if (serie) {
                favorites.push({
                  id: favorite.id,
                  contentDbId: serie.serie_id,
                  title: serie.title,
                  description: serie.description || 'No description available',
                  thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.serieCardVertical, `Favorite Serie: ${serie.title}`),
                  type: 'serie',
                  created_at: favorite.created_at,
                });
              }
            });
          }
        }
      }

      console.log(`🔍 [${new Date().toISOString()}] Fetching favorite episodes...`);
      // Get favorite episodes
      const { data: favoriteEpisodes, error: episodesError } = await withSupabaseResult<any[]>(
        supabase
          .from('user_favorites')
          .select('*')
          .eq('smartuser_id', smartuserId)
          .eq('content_type', 'episode')
          .order('created_at', { ascending: false }),
        { operation: 'favorites.db.favoriteEpisodes', logger: favLog, fallback: [] }
      );

      if (episodesError) {
        console.error('Error fetching favorite episodes:', episodesError);
      } else if (favoriteEpisodes) {
        // Get episode details for favorite episodes
        const episodeIds = favoriteEpisodes.map(fav => fav.content_id);
        console.log(`🔍 [${new Date().toISOString()}] Episode IDs to fetch details for:`, episodeIds);

        if (episodeIds.length > 0) {
          const cachedEpisodes = await episodeDetailsCacheService.getBulkEpisodeDetails(
            campaignCountriesLanguagesId,
            episodeIds
          );

          // Find which episodes are missing from cache
          const missingIds = episodeIds.filter(id => !cachedEpisodes.has(id));

          let episodeDetails: any[] = [];

          if (missingIds.length > 0) {
            console.log(`🔍 [${new Date().toISOString()}] Fetching ${missingIds.length} missing episodes from database`);

            const useUnified = isUnifiedContentEnabled();
            console.log(`🔍 [${new Date().toISOString()}] Using ${useUnified ? 'unified' : 'legacy'} content system`);

            if (useUnified) {
              console.log(`🔍 [${new Date().toISOString()}] Unified content system not yet fully implemented for favorites`);
            }

            const { data: fetchedDetails, error: episodeDetailsError } = await withSupabaseResult<any[]>(
              supabase
                .from('contents_series_episodes')
                .select(`
                  episode_id,
                  title,
                  description,
                  episode_position,
                  season_position,
                  duration,
                  series_id,
                  contents_series!inner(
                    title,
                    url_covers
                  )
                `)
                .in('episode_id', missingIds)
                .eq('campaign_countries_languages_id', campaignCountriesLanguagesId),
              { operation: 'favorites.db.episodeDetails', logger: favLog, fallback: [] }
            );

            if (episodeDetailsError) {
              console.error('Error fetching episode details:', episodeDetailsError);
            } else if (fetchedDetails) {
              await episodeDetailsCacheService.setBulkEpisodeDetails(
                campaignCountriesLanguagesId,
                fetchedDetails
              );
              episodeDetails = fetchedDetails;
            }
          }

          // Combine cached and fetched episodes
          const allEpisodeDetails = new Map<number, any>();
          cachedEpisodes.forEach((details, id) => allEpisodeDetails.set(id, details));
          episodeDetails.forEach((details: any) => allEpisodeDetails.set(details.episode_id, details));

          console.log(`🔍 [${new Date().toISOString()}] Episode details result:`, {
            cached: cachedEpisodes.size,
            fetched: episodeDetails.length,
            total: allEpisodeDetails.size
          });

          // Build favorites list
          favoriteEpisodes.forEach((favorite: any) => {
            const episode = allEpisodeDetails.get(favorite.content_id);
            if (episode) {
              const serie = episode.contents_series;
              favorites.push({
                id: favorite.id,
                contentDbId: episode.episode_id,
                title: episode.title || `Episode ${episode.episode_position}`,
                description: episode.description || `Episode ${episode.episode_position} description`,
                thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.serieCardVertical, `Favorite Episode: ${serie.title} S${episode.season_position}E${episode.episode_position}`),
                type: 'episode',
                created_at: favorite.created_at,
                episodeNumber: episode.episode_position,
                duration: Math.floor((episode.duration || 900) / 60),
                seasonNumber: episode.season_position,
                serieTitle: serie.title,
                serieId: episode.series_id.toString(),
              });
            }
          });
        }
      }

      // Sort all favorites by creation date
      favorites.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      console.log(`🔍 [${new Date().toISOString()}] Final favorites result:`, {
        totalFavorites: favorites.length,
        seriesCount: favorites.filter(f => f.type === 'serie').length,
        episodesCount: favorites.filter(f => f.type === 'episode').length,
        favorites: favorites.map(f => ({ id: f.id, title: f.title, type: f.type }))
      });

      return favorites;
    } catch (err) {
      console.error('Unexpected error fetching user favorites:', err);
      return [];
    }
  }

  static async checkFavoriteInDatabase(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<boolean> {
    if (!supabase) return false;
    const { data } = await withSupabaseResult<{ id: string } | null>(
      supabase
        .from('user_favorites')
        .select('id')
        .eq('smartuser_id', smartuserId)
        .eq('content_id', contentId)
        .eq('content_type', contentType)
        .maybeSingle(),
      { operation: 'favorites.db.checkInDatabase', logger: favLog }
    );
    return !!data;
  }

  static async toggleFavorite(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<{ success: boolean; isFavoriteAfter: boolean }> {
    console.log('❤️ FavoritesService.toggleFavorite called:', {
      smartuserId,
      contentId,
      contentType
    });

    let isFav = await this.isFavorite(smartuserId, contentId, contentType);
    console.log('❤️ FavoritesService.toggleFavorite: cache check result:', { isFav });

    if (!isFav) {
      const dbCheck = await this.checkFavoriteInDatabase(smartuserId, contentId, contentType);
      if (dbCheck) {
        console.log('❤️ FavoritesService.toggleFavorite: cache desync detected - DB has record, cache does not. Syncing cache.');
        isFav = true;
        favoritesCacheService.forceSync(smartuserId).catch(err =>
          console.error('❤️ Background cache sync error:', err)
        );
      }
    }

    if (isFav) {
      console.log('❤️ FavoritesService.toggleFavorite: Removing from favorites');
      const success = await this.removeFromFavorites(smartuserId, contentId, contentType);
      return { success, isFavoriteAfter: !success };
    } else {
      console.log('❤️ FavoritesService.toggleFavorite: Adding to favorites');
      const success = await this.addToFavorites(smartuserId, contentId, contentType);
      return { success, isFavoriteAfter: success };
    }
  }
}